// A workspace's icon, as the rail draws its tile: its picture where the screen
// has it (the cloud's web), else its first letter on one of six grounds.

import { m } from "@trameria/i18n";
import type { ReactNode } from "react";
import {
  TileMark,
  workspaceGroundIndex,
  workspaceInitial,
} from "../shell/rail";
import {
  useShowRowWorkspaces,
  useWorkspaces,
  type WorkspaceMark,
  type Workspaces,
} from "./queries";

export function WorkspaceTile({
  workspace,
  ws,
  size = 18,
}: {
  workspace: WorkspaceMark;
  ws: Workspaces;
  size?: number;
}) {
  return (
    <TileMark
      initial={workspaceInitial(workspace.name)}
      ground={workspaceGroundIndex(workspace.id)}
      image={ws.imageOf(workspace)}
      size={size}
    />
  );
}

/**
 * The icons of the workspaces `ids` in a list row's `end` (side by side,
 * overlapping: a person is in several). Nothing for a thing with none, for
 * one nobody remembers, and in a list narrowed to one workspace.
 */
export function RowWorkspaces({
  ids,
  className = "ml-2",
}: {
  ids: readonly (string | null | undefined)[];
  className?: string;
}): ReactNode {
  const show = useShowRowWorkspaces();
  const ws = useWorkspaces();
  if (!show) return null;
  const known = ids.flatMap((id) => (id ? (ws.byId.get(id) ?? []) : []));
  if (known.length === 0) return null;
  return (
    <span
      data-workspace-icons=""
      title={known.map((w) => w.name).join(", ")}
      className={`inline-flex flex-none items-center ${className}`}
    >
      {known.map((w, i) => (
        <span
          key={w.id}
          className={`inline-flex rounded-full ring-2 ring-surface ${i > 0 ? "-ml-1" : ""}`}
        >
          <WorkspaceTile workspace={w} ws={ws} size={16} />
        </span>
      ))}
    </span>
  );
}

/** The WS column's width in a table (room for three overlapping icons). */
export const WS_COL = "w-[56px] flex-none";

/**
 * Whether a table has a WS column: in Personal's lists only, and once some
 * workspace is known (a Vault that never heard of one has nothing to show).
 */
export function useWorkspaceColumn(): boolean {
  const show = useShowRowWorkspaces();
  const ws = useWorkspaces();
  return show && ws.byId.size > 0;
}

/** The WS column's head. */
export function WorkspaceColumnHead({
  className = "",
}: {
  className?: string;
}) {
  if (!useWorkspaceColumn()) return null;
  return (
    <span className={`${WS_COL} truncate ${className}`}>
      {m.shell_workspace_col()}
    </span>
  );
}

/** A row's cell of the WS column (empty for a row with no workspace). */
export function WorkspaceCell({
  ids,
  className = "",
}: {
  ids: readonly (string | null | undefined)[];
  className?: string;
}) {
  if (!useWorkspaceColumn()) return null;
  return (
    <span className={`${WS_COL} flex items-center ${className}`}>
      <RowWorkspaces ids={ids} className="" />
    </span>
  );
}
